import { PropTypes } from 'typed/imports';
import { ComponentEventHandler, PureComponent, ReactEventHandler } from 'typed/components/utils';
import { FormattedResource } from 'typed/components/common';
import { MarkdownBlock, Tooltip } from 'components/common';
import './MultilineEllipsis.less';

interface OwnProps {
    className?: string;
    value: string;
    maxHeight: number;
    markdown?: boolean;
    overflowTooltip?: boolean;
    showMoreButton?: boolean;
    showLessButton?: boolean;
    onShowMore?: ComponentEventHandler;
}
interface Props extends OwnProps { }

interface State {
    overflows: boolean;
    allTextVisible: boolean;
}

export default class MultilineEllipsis extends PureComponent<Props, State> {
    static readonly propTypes = {
        className: PropTypes.string,
        value: FormattedResource.resourcePropType.isRequired,
        maxHeight: PropTypes.number.isRequired,
        markdown: PropTypes.bool,
        overflowTooltip: PropTypes.bool,
        showMoreButton: PropTypes.bool,
        showLessButton: PropTypes.bool
    };

    static readonly defaultProps = {
        markdown: false,
        overflowTooltip: true,
        showMoreButton: false,
        showLessButton: false
    };

    state: State = {
        overflows: false,
        allTextVisible: false
    };


    componentDidMount() {
        this.componentDidUpdate();
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return this.props.className !== nextProps.className || this.props.value !== nextProps.value || this.props.maxHeight !== nextProps.maxHeight ||
            this.state.overflows !== nextState.overflows || this.state.allTextVisible !== nextState.allTextVisible;
    }

    componentDidUpdate() {
        const
            root = this.refs.root as unknown as HTMLElement | undefined,
            text = this.refs.text as unknown as HTMLElement | undefined;

        if (root && text) {
            const overflows = text.getBoundingClientRect().height > this.props.maxHeight;
            if (overflows !== this.state.overflows) { this.setState({ overflows }); }
        }
    }

    private _onShowMore: ReactEventHandler = (event) => {
        event.stopPropagation();

        if (this.props.onShowMore) {
            this.props.onShowMore(this);
        }
        else {
            this.setState({ allTextVisible: true });
        }
    };

    private _onShowLessClick: ReactEventHandler = (event) => {
        event.stopPropagation();
        this.setState({ allTextVisible: false });
    };

    render() {
        const
            { className, value, maxHeight, markdown, overflowTooltip, showMoreButton, showLessButton } = this.props,
            { overflows, allTextVisible } = this.state,

            showMoreButtonVisible = overflows && !allTextVisible && showMoreButton,
            showLessButtonVisible = overflows && allTextVisible && showLessButton;

        return (
            <div
                ref="root"
                className={`multiline-ellipsis${className ? ` ${className}` : ''}${overflows && !allTextVisible ? ' overflows' : ''}${showMoreButtonVisible ? ' with-show-more' : ''}`}
                style={{ maxHeight: overflows && !allTextVisible ? `${maxHeight}px` : '' }}
            >
                <div ref="text" className="text">
                    {
                        markdown ?
                            <MarkdownBlock value={value} /> :
                            <FormattedResource resource={value} />
                    }
                </div>
                {
                    showMoreButtonVisible ?
                        <button className="inline show-more" onClick={this._onShowMore}>
                            <FormattedResource resource={{ id: 'common.show_more' }} />
                        </button> :
                        null
                }
                {
                    showLessButtonVisible ?
                        <button className="inline" onClick={this._onShowLessClick}>
                            <FormattedResource resource={{ id: 'common.show_less' }} />
                        </button> :
                        null
                }
                {
                    overflows && !allTextVisible && overflowTooltip ?
                        <Tooltip><FormattedResource resource={value} /></Tooltip>
                        : null
                }
            </div>
        );
    }
}
